import React, { useEffect, useMemo, useState } from 'react';
import { graphql } from 'gatsby';
import { Link } from '../components/Link/Link';

import { useLocation } from '@reach/router';
import Layout from '../components/Layout';
import SEO from '../components/seo';
import SideBar from '../components/SideBar/SideBar';
import PageTitle from '../components/PageTitle/PageTitle';
import Paper from '../components/Paper/Paper';
import Banner from '../components/Banner/Banner';
import ConnectAndContribute from '../components/ConnectAndContribute/ConnectAndContribute';
import { BACK, MICROPYTHON_PAGE, PROGRAMMING_PAGE, SIDEBAR_MYCROPYTHON, SIDEBAR_PROGRAMMING } from '../utils/constants';

import IDE2Img from '../assets/Micropython/code.svg';
import OpenMV from '../assets/getting-started.svg';

import './micropython.scss';
import VideoWithInfo from '../components/VideoWithInfo/VideoWithInfo';
import { IconArrowUpRightTeal } from '../components/Icons';
import BgBanner from '../components/BgBanner/BgBanner';
import PageSubtitle from '../components/PageSubtitle/PageSubtitle';
import { getMicropythonSideBarDataRefactored } from '../utils/functions';
import MicropythonFirmwares from '../components/MicropythonFirmwares/MicropythonFirmwares';
import MicropythonFirmwaresMobile from '../components/MicropythonFirmwares/MicropythonFirmwaresMobile';
import Breadcrumbs from '../components/Breadcrumbs/Breadcrumbs';
import MicroPythonAccordion from '../components/MicropythonAccordion/MicroPyAccordion';

export default function MicroPython({ data }: any): React.ReactElement {
  const uPythonReleasesEndpoint = `https://downloads.arduino.cc/micropython/index.json?nonce=${Math.floor(Math.random() * 10000000)}`;
  const [releasesData, setReleasesData] = useState(null);
  const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 580 : false;

  const location = useLocation();

  useEffect(() => {
    fetch(uPythonReleasesEndpoint, { cache: 'no-cache' })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('Network error occurred.' + releasesData);
      })
      .then((data) => {
        if (!releasesData) setReleasesData(data);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        throw new Error(`There has been a problem with fetching the JSON data: ${error}`);
      });
  }, [releasesData, uPythonReleasesEndpoint]);

  const microPythonRefactoredData = useMemo(() => {
    return getMicropythonSideBarDataRefactored(data, data.images);
  }, [data]);

  useEffect(() => {
    if (location.hash !== '') {
      var element_to_scroll_to = document.getElementById('micropython101');
      element_to_scroll_to.scrollIntoView();
    }
  }, [location, location.hash]);

  return (
    <Layout>
      <SEO title="MicroPython" description="We support the official MicroPython project by contributing to the upstream repo." />
      <main className="page-container">
        <SideBar
          links={[]}
          nestedCategories={microPythonRefactoredData}
          actualPage={'MicroPython'}
          prevPage={{ title: BACK, pathname: '/programming/' }}
        />
        <div className="page-layout micropython">
          <Breadcrumbs
            actualPage={SIDEBAR_MYCROPYTHON}
            list={[
              { title: 'Home', url: '/' },
              { title: SIDEBAR_PROGRAMMING, url: PROGRAMMING_PAGE },
              { title: SIDEBAR_MYCROPYTHON, url: MICROPYTHON_PAGE },
            ]}
          />
          <PageTitle
            title="MicroPython"
            description="Arduino is adding the Python language as an additional option for programming microcontrollers. Our platform of choice is  <a href='https://micropython.org/'>MicroPython</a>. We support the official MicroPython project by contributing to the upstream repo."
          />
          <Paper>
            To load MicroPython scripts to your board, you need to use a code editor. There are two editors to choose from:{' '}
            <Link href="https://labs.arduino.cc/en/labs/micropython" className="micropython--normal-weight">
              Arduino Lab for MicroPython
            </Link>{' '}
            and the{' '}
            <Link href="https://openmv.io/pages/download" className="micropython--normal-weight">
              OpenMV
            </Link>
          </Paper>
          <VideoWithInfo video={data.video.childMdx.frontmatter.url} info={data.video.childMdx.frontmatter.info} />
          <Banner
            title="Arduino Lab for MicroPython"
            description="Explore Arduino Lab for MicroPython, an experimental, lightweight editor, suitable for most users that aim to write standard MicroPython code."
            image={IDE2Img}
            cta={[{ url: 'https://labs.arduino.cc/en/labs/micropython', label: 'DOWNLOAD IDE', icon: <IconArrowUpRightTeal />, external: true }]}
          />
          <a id="micropython101" href="#micropython101">
            {' '}
          </a>
          <MicroPythonAccordion pyData={microPythonRefactoredData} images={data.images} descriptions={data.descriptions} />
          <PageSubtitle
            title="Firmware"
            description="Arduino has developed MicroPython support for the boards listed below. Here you can find the latest firmwares, and a link to installation instructions."
          />
          {releasesData && !isMobile && <MicropythonFirmwares firmwareData={releasesData?.boards} />}
          {releasesData && isMobile && <MicropythonFirmwaresMobile firmwareData={releasesData?.boards} />}
          {/* <section className="micropython__columns">
            {releasesData &&
              releasesData.boards.map((board) => {
                return (
                  <div key={board.displayname} className="micropython__columns-item">
                    <h3>{board.displayname}</h3>
                    {board.releases.map((release) => {
                      return (
                        <div key={release.name}>
                          <a href={release.source}>
                            {release.type} {release.name}
                          </a>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
          </section> */}
          <BgBanner
            title="OpenMV Firmware & IDE"
            description="OpenMV is a MicroPython-based firmware for machine vision and learning. It includes a specialized editor. Install the OpenMV firmware for Arduino via the IDE and find the latest versions on the OpenMV release page on GitHub. Check it out!"
            image={OpenMV}
            cta={{ url: 'https://github.com/openmv/openmv/releases', label: 'OPENMV GITHUB', icon: <IconArrowUpRightTeal />, external: true }}
          />
          {/*
          <PageSubtitle title="Tutorials" />
           <section className="micropython__columns">
            {microPythonTutorials.boards.map((board) => {
              return (
                <div key={board.displayname} className="micropython__columns-item">
                  <h3>{board.displayname}</h3>
                  {board.releases.map((release) => {
                    return (
                      <div key={release.name}>
                        <a href={release.source}>
                          {release?.type} {release.name}
                        </a>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </section> */}
          <ConnectAndContribute />
        </div>
      </main>
    </Layout>
  );
}

export const pageQuery = graphql`
  {
    micropythonPages: allFile(filter: { fields: { type: { eq: "micropython" } } }, sort: { relativeDirectory: ASC }) {
      edges {
        node {
          relativeDirectory
          name
          fields {
            slug
            gitLogLatestDate
          }
          childMdx {
            frontmatter {
              title
              description
              author
              icon
            }
          }
        }
      }
    }
    images: allFile(filter: {extension: {regex: "/(jpg)|(jpeg)|(png)/"}, base: {regex: "/\\py-hero-banner.(jpe?g|png|gif|bmp|svg)$/i"}}) {
      edges {
        node {
          id
          relativeDirectory
          childImageSharp {
            gatsbyImageData(quality: 100, placeholder: BLURRED)
          }
        }
      }
    }
    descriptions:  allFile(filter: { base: { eq: "micropython-content.md" } }, sort: { fields: { order: ASC } }) {
      edges {
        node {
          relativeDirectory
          name
          childMdx {
            frontmatter {
              title
              description
            }
          }
        }
      }
    }
    video: file(relativePath: { eq: "micropython/videoInfo.md" }) {
      relativeDirectory
      childMdx {
        body
        frontmatter {
          url
          info {
            icon
            title
            description
            link
          }
        }
      }
    }
  }
`;
